import React, { useMemo, useState } from 'react';
import { Col, Input, Row, Table } from '../../../components/ui';
import { useListingUnsubscription } from 'query/unsubscribe';
import useDebounce from 'react-use/lib/useDebounce';
import ManualUnsubscribeForm from './ManualUnsubscribeForm';
import moment from 'moment';
import ResubscribeButton from './ResubscribeButton';
import UserWithAvatar from '../../../components/ui/UserWithAvatar';

function UnsubscribedByRender({ unsubscriptionRecord }) {
  switch (unsubscriptionRecord.eventSource) {
    case 'interseller':
      return 'Interseller';
    case 'failure':
      return 'Failure Emails';
    case 'admin':
      if (!unsubscriptionRecord.updatedBy) return 'Unknown';
      return <UserWithAvatar user={unsubscriptionRecord.updatedBy} />;
    case 'request':
      return 'Self Request';
    case 'pause':
      return 'Pause';
    case 'pause_only_page':
      return 'Pause (Pause Only UI)';
    default:
      return unsubscriptionRecord.eventSource || 'Unknown';
  }
}

export default function SingleUnsubscriptionSection() {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(5);

  useDebounce(() => setDebouncedSearch(search), 500, [search]);

  const {
    data: unsubscribers,
    isFetching: listLoading,
  } = useListingUnsubscription({
    search: debouncedSearch,
    page,
    limit,
  });

  const tableLoading = listLoading;

  const renderSearch = useMemo(() => {
    return (
      <Row gutter={8}>
        <Col span={16}>
          <ManualUnsubscribeForm />
        </Col>
        <Col span={8}>
          <p style={{ marginBottom: 5 }}>Search for unsubscribers</p>
          <Input
            id="email-addresses"
            name="email"
            placeholder="abc@xyz.com"
            value={search}
            onChange={e => {
              setSearch(e.target.value);
            }}
          />
        </Col>
      </Row>
    );
  }, [search]);

  const tableColumns = useMemo(() => {
    return [
      {
        title: 'Id',
        dataIndex: 'id',
        key: 'id',
      },
      {
        title: 'Email',
        dataIndex: 'email',
        key: 'email',
      },
      {
        title: 'actions',
        render: (data, { id }) => <ResubscribeButton id={id} />,
      },
      {
        title: 'First Unsubscribed On',
        dataIndex: 'createdDate',
        key: 'createdDate',
        render: date => {
          return moment(date).format('YYYY-MM-DD HH:mm');
        },
      },
      {
        title: 'Last Unsubscribed On',
        dataIndex: 'updatedDate',
        key: 'updatedDate',
        render: date => {
          return moment(date).format('YYYY-MM-DD HH:mm');
        },
      },
      {
        title: 'Unsubscribed By',
        dataIndex: 'updatedBy',
        key: 'updatedBy',
        render: (_, data) => {
          return <UnsubscribedByRender unsubscriptionRecord={data} />;
        },
      },
    ];
  }, []);

  const renderTable = useMemo(() => {
    return (
      <Row>
        <Table
          columns={tableColumns}
          dataSource={unsubscribers}
          id="unsubscribers"
          loading={tableLoading}
          pagination={{
            total: 5000,
            current: page,
            pageSize: limit,
            onChange: newPage => {
              setPage(newPage);
            },
            onShowSizeChange: (current, size) => {
              setPage(1);
              setLimit(size);
            },
          }}
          rowKey="id"
        />
      </Row>
    );
  }, [tableColumns, tableLoading, unsubscribers]);

  return (
    <>
      {renderSearch}
      {renderTable}
    </>
  );
}
